/**
 * DevTierSwitcher — floating widget that lets dev accounts (DEV_EMAILS in
 * SubscriptionContext) switch their own subscription tier in-place, with no
 * Stripe checkout.
 *
 * This is NOT a client-side mock. Each change POSTs to the real backend
 * `/api/subscription/admin/set-override-tier`, which sets the `override_tier`
 * column on the user's row. `get_effective_tier` reads that column, so every
 * subsequent entitlement check, paywall, quota chip, and usage counter behaves
 * exactly as it would for a paying customer on that tier.
 *
 * After a switch we refresh BOTH the subscription (tier label + feature flags)
 * AND the usage summary (the quota chips + limits every gated surface reads via
 * useQuota) — the old version only refreshed the former, so limits appeared not
 * to change until a full page reload.
 */
import React, { useState } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { BeakerIcon, CheckIcon, XIcon, RefreshIcon } from '@heroicons/react/outline';
import {
  useSubscription,
  SubscriptionTier,
  TierInfo,
} from '../../contexts/SubscriptionContext';
import apiConfig from '../../config/apiConfig';
import { tierName, tierPrice } from '../subscription/tierConfig';

type Choice = SubscriptionTier | 'real';

const TIERS: SubscriptionTier[] = ['free', 'plus', 'pro', 'studio'];

/** "$0" · "$17/mo" · "$40/mo" · "custom": the plan's price from the one tier mirror (the server's once /tiers lands). */
const priceLabel = (tier: SubscriptionTier, tiers?: TierInfo[]): string => {
  const p = tierPrice(tier, tiers);
  return p < 0 ? 'custom' : p === 0 ? '$0' : `$${p}/mo`;
};

// One-line summary for a tier, from the real /tiers reply. Keeps the switcher
// honest — it shows what the gates enforce, in the plan's own words (chat v5):
// chat as the server's "about N questions" note (the bar is the truth; never a
// count and never "unlimited") and reports as a number a month. Retired meters
// (coach_run, pitch_drafts, prospect_leads) are never shown, not even here.
export function limitSummary(t: TierInfo | undefined): string {
  if (!t) return '';
  const chat = t.questions_note ? t.questions_note.replace(/ a month( per creator)?$/, '') : 'chat';
  const reports = typeof t.limits.reports === 'number' && t.limits.reports >= 0 ? String(t.limits.reports) : '—';
  return `${chat} · ${reports} reports / mo`;
}

export const DevTierSwitcher: React.FC = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { subscription, allTiers, refreshSubscription, refreshUsageSummary, isDev } = useSubscription();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<Choice | null>(null);
  const [justApplied, setJustApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isDev || !user?.id) return null;

  const effectiveTier = subscription?.tier ?? 'free';
  const overrideActive = !!subscription?.override_tier || !!subscription?.is_test_account;
  const realTier = subscription?.real_tier ?? subscription?.tier ?? 'free';
  const isTestAccount = !!subscription?.is_test_account;
  const tierInfo = (v: SubscriptionTier) => allTiers.find((t) => t.tier === v);

  const apply = async (choice: Choice) => {
    if (pending) return;
    setPending(choice);
    setError(null);
    setJustApplied(false);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not signed in — refresh and try again.');
      const res = await fetch(`${apiConfig.baseUrl}/api/subscription/admin/set-override-tier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        // `null` clears the override (revert to the real subscription).
        body: JSON.stringify({ user_id: user.id, tier: choice === 'real' ? null : choice }),
      });
      if (!res.ok) {
        const body = await res.text();
        const hint = res.status === 403
          ? 'Your account is not the configured admin (ADMIN_USER_ID) on the API.'
          : body.slice(0, 140);
        throw new Error(`HTTP ${res.status}: ${hint}`);
      }
      // Refresh BOTH: subscription (tier + feature flags) and the usage summary
      // (quota chips + limits). Without the second, limits look unchanged.
      await Promise.all([refreshSubscription(), refreshUsageSummary()]);
      setJustApplied(true);
      window.setTimeout(() => setJustApplied(false), 1600);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch tier');
    } finally {
      setPending(null);
    }
  };

  // ── Collapsed pill ──
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border border-border-light bg-white px-3.5 py-2 shadow-lg transition hover:border-bearriBlue/50 hover:shadow-xl"
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-bearriBlue-50 text-bearriBlue">
          <BeakerIcon className="h-3.5 w-3.5" />
        </span>
        <span className="text-caption font-semibold text-text-muted">Dev</span>
        <span className="text-body-sm font-bold text-text-primary">{subscription?.display_name ?? '—'}</span>
        {overrideActive && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="Override active" />}
      </button>
    );
  }

  // ── Expanded panel ──
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 overflow-hidden rounded-2xl border border-border-light bg-white shadow-2xl" style={{ fontFeatureSettings: '"tnum"' }}>
      <div className="flex items-start justify-between gap-2 border-b border-border-light bg-bearriBlue-50/60 px-4 py-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wider text-bearriBlue-dark">
            <BeakerIcon className="h-3.5 w-3.5" /> Dev · Tier switcher
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="font-display text-h4 font-bold text-text-primary">{subscription?.display_name ?? '—'}</span>
            {overrideActive ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                {isTestAccount ? 'test account' : 'override'}
              </span>
            ) : (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">real</span>
            )}
          </div>
          {overrideActive && !isTestAccount && (
            <div className="mt-0.5 text-caption text-text-muted">real subscription: {realTier}</div>
          )}
        </div>
        <button onClick={() => setOpen(false)} className="rounded-lg p-1 text-text-muted hover:bg-white hover:text-text-primary" aria-label="Close">
          <XIcon className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} />
        </button>
      </div>

      {isTestAccount && (
        <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-[11px] text-amber-800">
          This account is flagged <code>is_test_account</code> → always Studio. Tier overrides are ignored until that's cleared.
        </div>
      )}

      <ul className="max-h-[52vh] overflow-y-auto py-1">
        {TIERS.map((value) => {
          const t = { value, label: tierName(value, allTiers), price: priceLabel(value, allTiers) };
          const isCurrent = effectiveTier === t.value;
          const isPending = pending === t.value;
          return (
            <li key={t.value}>
              <button
                onClick={() => apply(t.value)}
                disabled={!!pending}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition
                  ${isCurrent ? 'bg-bearriBlue-50/70' : 'hover:bg-bearriBlue-50/40'}
                  ${pending && !isPending ? 'opacity-40' : ''}`}
              >
                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-body-sm font-extrabold
                  ${isCurrent ? 'bg-bearriBlue text-white' : 'bg-surface-muted text-text-secondary'}`}>
                  {t.label.charAt(0)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-body-sm font-bold text-text-primary">{t.label}</span>
                    <span className="text-caption text-text-disabled">{t.price}</span>
                  </span>
                  <span className="block truncate text-caption text-text-muted">{limitSummary(tierInfo(t.value))}</span>
                </span>
                {isPending ? (
                  <RefreshIcon className="h-4 w-4 flex-shrink-0 animate-spin text-bearriBlue" />
                ) : isCurrent ? (
                  <CheckIcon className="h-4 w-4 flex-shrink-0 text-bearriBlue" />
                ) : null}
              </button>
            </li>
          );
        })}

        {/* Clear override → back to the real subscription */}
        <li className="mt-1 border-t border-border-light pt-1">
          <button
            onClick={() => apply('real')}
            disabled={!!pending || !overrideActive}
            className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition
              ${!overrideActive ? 'cursor-default opacity-50' : 'hover:bg-bearriBlue-50/40'}`}
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-surface-muted text-text-secondary">
              <RefreshIcon className={`h-4 w-4 ${pending === 'real' ? 'animate-spin text-bearriBlue' : ''}`} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-body-sm font-bold text-text-primary">Use real subscription</span>
              <span className="block truncate text-caption text-text-muted">{overrideActive ? `Clears the override → ${realTier}` : 'No override active'}</span>
            </span>
          </button>
        </li>
      </ul>

      {error && (
        <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-[11px] leading-snug text-red-700">{error}</div>
      )}
      {justApplied && !error && (
        <div className="flex items-center gap-1.5 border-t border-emerald-100 bg-emerald-50 px-4 py-2 text-[11px] font-semibold text-emerald-700">
          <CheckIcon className="h-3.5 w-3.5" /> Applied — limits & quota chips updated live.
        </div>
      )}
      <div className="border-t border-border-light bg-surface-muted px-4 py-2 text-[11px] leading-snug text-text-muted">
        Sets a server-side <code>override_tier</code> — no Stripe, no charge. Entitlement checks, paywalls & usage counters all respect it exactly like a real subscriber.
      </div>
    </div>
  );
};

export default DevTierSwitcher;
